import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Row, Col } from 'react-flexbox-grid';
import config from '../../config';
import TableRankingIndividualItem from '../TableRankingIndividualItem';
import EmptyState from '../EmptyState';
import { handleResponse, serializeQuery } from '../../utils/handleResponse';

const TableCalendarIndividual = ({
  raceDay,
  raceEvent,
  setShowLoading,
  setShowSecondLoading,
  individualFavorites,
  saveFavorite,
  deleteFavorite,
  selectedGenderFilter,
  selectedIndividualCategoryFilter,
  searchText,
  changeIndividual,
  favoritesEnabled,
}) => {
  const [startingTimes, setStartingTimes] = useState([]);
  const [startingTimesFavorite, setStartingTimesFavorite] = useState([]);
  const normalizeGender = (value) => {
    const gender = String(value || '')
      .trim()
      .toUpperCase();
    if (['M', 'MALE', 'MEN', 'HOMBRE'].includes(gender)) return 'male';
    if (['F', 'FEMALE', 'WOMEN', 'MUJER'].includes(gender)) return 'female';
    return null;
  };

  const applyIndividualFilters = (schedules) => {
    const filteredByGender = selectedGenderFilter
      ? schedules.filter(
          (schedule) =>
            normalizeGender(schedule?.participant?.gender) === selectedGenderFilter
        )
      : schedules;

    if (selectedIndividualCategoryFilter !== 'PRO') return filteredByGender;

    return filteredByGender.filter((schedule) =>
      String(schedule?.participant?.category || '')
        .toUpperCase()
        .includes('PRO')
    );
  };

  const applyProFilter = (schedules) => {
    return applyIndividualFilters(schedules);
  };

  const normalizeSearch = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const applySearchFilter = (schedules) => {
    const term = normalizeSearch(searchText).trim();
    if (!term) return schedules;
    return schedules.filter((schedule) => {
      const participant = schedule?.participant || {};
      const fullName = normalizeSearch(
        `${participant?.name || ''} ${participant?.lastName || ''}`
      );
      const teamName = normalizeSearch(participant?.team?.name);
      return fullName.includes(term) || teamName.includes(term);
    });
  };

  const getCalendar = async (user) => {
    setShowLoading(true);
    let athlete = null;
    try {
      athlete = JSON.parse(localStorage.getItem('userData') || 'null');
    } catch (e) {
      athlete = null;
    }
    const headers = athlete?.userToken
      ? {
          Authorization: `Bearer ${athlete.userToken}`,
          'Content-Type': 'application/json',
        }
      : undefined;

    const requestOptions = {
      method: 'GET',
      ...(headers ? { headers } : {}),
    };

    const params = {
      day: raceDay,
    };

    if (selectedGenderFilter) {
      if (!params['query']) {
        params['query'] = {};
      }
      params['query']['gender'] = selectedGenderFilter;
    }

    if (!params['query']) {
      params['query'] = {};
    }

    const url = new URL(
      `${config.tncApiUrl}/sport-events/${config.eventId}/schedules`
    );
    url.search = serializeQuery(params);

    const response = await fetch(url, requestOptions);
    setShowLoading(false);
    try {
      const newStartingTimes = await handleResponse(response);
      const schedules = (newStartingTimes?.data?.schedules || []).filter(
        (schedule) => !!schedule?.participant
      );
      setStartingTimes(applyProFilter(schedules));
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!startingTimes.length || !individualFavorites.length) {
      setStartingTimesFavorite([]);
      return;
    }

    // IDs (userId) que el usuario marcó como favoritos
    const favIdSet = new Set(individualFavorites.map((f) => f.favoriteId));
    const favTimes = startingTimes.filter((st) =>
      favIdSet.has(st.participant?.userId)
    );
    console.log('favTimes', favTimes);
    setStartingTimesFavorite(favTimes);
  }, [startingTimes, individualFavorites]);

  useEffect(() => {
    if (raceDay) {
      getCalendar();
    }
  }, [raceDay]);

  useEffect(() => {
    if (changeIndividual) {
      getCalendar();
    }
  }, [changeIndividual]);

  const visibleStartingTimes = applySearchFilter(startingTimes);
  const visibleStartingTimesFavorite = applySearchFilter(startingTimesFavorite);

  return (
    <Col xs={12}>
      <Row>
        {raceDay &&
          visibleStartingTimes.length > 0 &&
          visibleStartingTimesFavorite.map((startingTime, index) => {
            return (
              <TableRankingIndividualItem
                userInfo={{ ranking: index + 1, ...startingTime }}
                key={startingTime.id}
                firstRaking={startingTime[0]}
                individualFavorites={individualFavorites}
                saveFavorite={saveFavorite}
                deleteFavorite={deleteFavorite}
                calendar
                darkMode
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
        {!raceDay || (visibleStartingTimes.length === 0 && <EmptyState />)}
        {raceDay &&
          visibleStartingTimes.map((startingTime, index) => {
            if (!startingTime?.participant) return null;
            return (
              <TableRankingIndividualItem
                userInfo={{ ranking: index + 1, ...startingTime }}
                key={startingTime.id}
                firstRaking={startingTime[0]}
                individualFavorites={individualFavorites}
                saveFavorite={saveFavorite}
                deleteFavorite={deleteFavorite}
                calendar
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
      </Row>
    </Col>
  );
};

TableCalendarIndividual.propTypes = {
  setShowLoading: PropTypes.func.isRequired,
  saveFavorite: PropTypes.func.isRequired,
  deleteFavorite: PropTypes.func.isRequired,
  setShowSecondLoading: PropTypes.func.isRequired,
  raceDay: PropTypes.number.isRequired,
  raceEvent: PropTypes.object.isRequired,
  individualFavorites: PropTypes.array.isRequired,
  selectedGenderFilter: PropTypes.string,
  selectedIndividualCategoryFilter: PropTypes.string,
  searchText: PropTypes.string,
  changeIndividual: PropTypes.number,
  favoritesEnabled: PropTypes.bool,
};

TableCalendarIndividual.defaultProps = {
  selectedGenderFilter: null,
  selectedIndividualCategoryFilter: null,
  searchText: '',
  changeIndividual: null,
  favoritesEnabled: false,
};

export default TableCalendarIndividual;
